import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import { SortableTh } from '../components/SortableTh';
import { useAuth } from '../context/AuthContext';
import { createEvent, fetchEvents, type EventRow } from '../api/events';
import { formatTaskDate } from '../utils/formatTaskDate';
import { useTableSortState } from '../hooks/useTableSort';
import { sortRows } from '../utils/tableSort';
import {
  calendarGridDates,
  dayCoversSpan,
  formatCalendarMonthTitle,
  formatWeekRangeTitle,
  isSameLocalDay,
  startOfLocalDayFromTaskField,
  startOfLocalWeekSunday,
  weekDatesFromSundayStart,
} from '../utils/taskCalendarSpan';
import './Page.css';
import './TasksBoard.css';
import './Events.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
type CalendarViewMode = 'month' | 'week';

function eventSpan(ev: EventRow): { start: Date; end: Date } | null {
  const s = startOfLocalDayFromTaskField(ev.start_date);
  const e = startOfLocalDayFromTaskField(ev.end_date);
  if (!s && !e) return null;
  const start = s ?? e!;
  const end = e ?? s!;
  if (start.getTime() > end.getTime()) return { start: end, end: start };
  return { start, end };
}

function eventHue(id: number): number {
  return (id * 53) % 360;
}

function formatDateTime(date: string | null, time: string | null): string {
  const d = formatTaskDate(date);
  if (!date) return '—';
  return time ? `${d} · ${time}` : d;
}

function EventsTable({
  title,
  sectionId,
  events,
  emptyText,
  onRowClick,
}: {
  title: string;
  sectionId: string;
  events: EventRow[];
  emptyText: string;
  onRowClick: (id: number) => void;
}) {
  const headingId = `events-section-${sectionId}`;
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(events, sortKey, sortDir, (ev, key) => {
        switch (key) {
          case 'name':
            return ev.name ?? '';
          case 'location':
            return ev.location ?? '';
          case 'start':
            return ev.start_date ?? '';
          case 'end':
            return ev.end_date ?? '';
          case 'owner':
            return ev.created_by_username ?? '';
          default:
            return '';
        }
      }),
    [events, sortKey, sortDir]
  );
  return (
    <section className="events-table-section" aria-labelledby={headingId}>
      <h2 className="events-section-title" id={headingId}>
        {title} <span className="events-section-count">({events.length})</span>
      </h2>
      <div className="events-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              <SortableTh label="Event name" columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Location" columnKey="location" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Start" columnKey="start" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="End" columnKey="end" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Created by" columnKey="owner" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={5} className="events-empty-cell">
                  {emptyText}
                </td>
              </tr>
            ) : (
              sorted.map((ev) => (
                <tr key={ev.id} className="events-table-row-clickable" onClick={() => onRowClick(ev.id)}>
                  <td data-label="Event name">{ev.name}</td>
                  <td data-label="Location">{ev.location || '—'}</td>
                  <td data-label="Start">{formatDateTime(ev.start_date, ev.start_time)}</td>
                  <td data-label="End">{formatDateTime(ev.end_date, ev.end_time)}</td>
                  <td data-label="Created by">{ev.created_by_username ?? '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const Events: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [active, setActive] = useState<EventRow[]>([]);
  const [past, setPast] = useState<EventRow[]>([]);
  const [voided, setVoided] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [viewMode, setViewMode] = useState<CalendarViewMode>('month');
  const [refDate, setRefDate] = useState(() => new Date());

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [newStartTime, setNewStartTime] = useState('');
  const [newEndTime, setNewEndTime] = useState('');
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchEvents(user.id);
      setActive(data.active);
      setPast(data.past);
      setVoided(data.void);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load events.');
      setActive([]);
      setPast([]);
      setVoided([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Calendar shows scheduled (non-void) events.
  const calendarEvents = useMemo(() => [...active, ...past], [active, past]);

  const calendarDays = useMemo(() => {
    if (viewMode === 'month') {
      return calendarGridDates(refDate.getFullYear(), refDate.getMonth());
    }
    return weekDatesFromSundayStart(startOfLocalWeekSunday(refDate));
  }, [viewMode, refDate]);

  const calendarTitle =
    viewMode === 'month'
      ? formatCalendarMonthTitle(refDate.getFullYear(), refDate.getMonth())
      : formatWeekRangeTitle(startOfLocalWeekSunday(refDate));

  const eventsForDay = useCallback(
    (day: Date) =>
      calendarEvents.filter((ev) => {
        const span = eventSpan(ev);
        return span != null && dayCoversSpan(day, span);
      }),
    [calendarEvents]
  );

  const goPrev = () => {
    setRefDate((d) => {
      const next = new Date(d);
      if (viewMode === 'month') next.setMonth(d.getMonth() - 1);
      else next.setDate(d.getDate() - 7);
      return next;
    });
  };
  const goNext = () => {
    setRefDate((d) => {
      const next = new Date(d);
      if (viewMode === 'month') next.setMonth(d.getMonth() + 1);
      else next.setDate(d.getDate() + 7);
      return next;
    });
  };
  const goToday = () => setRefDate(new Date());

  const openAdd = () => {
    setNewName('');
    setNewLocation('');
    setNewStartDate('');
    setNewEndDate('');
    setNewStartTime('');
    setNewEndTime('');
    setAddError('');
    setAddOpen(true);
  };

  const submitAdd = async () => {
    if (!user?.id) return;
    const name = newName.trim();
    if (!name) {
      setAddError('Event name is required.');
      return;
    }
    if (newStartDate && newEndDate && newEndDate < newStartDate) {
      setAddError('End date cannot be before the start date.');
      return;
    }
    setAddSubmitting(true);
    setAddError('');
    try {
      const created = await createEvent({
        user_id: user.id,
        name,
        location: newLocation.trim() || null,
        start_date: newStartDate || null,
        end_date: newEndDate || null,
        start_time: newStartTime || null,
        end_time: newEndTime || null,
      });
      setAddOpen(false);
      navigate(`/events/${created.id}`);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to create event.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const today = new Date();

  return (
    <div className="page-container">
      <div className="page-layout">
        <Sidebar />
        <main className="page-main">
          <PageHeader title="Events" subtitle="Plan events and capture attendee intake" />
          <div className="page-content">
            {!user ? (
              <p className="page-subtitle">Log in to view events.</p>
            ) : (
              <>
                <div className="page-toolbar events-page-toolbar">
                  <div className="events-toolbar-spacer" aria-hidden="true" />
                  <button type="button" className="page-toolbar-btn" onClick={openAdd}>
                    +&nbsp; Create event
                  </button>
                </div>

                {loadError ? (
                  <p className="create-order-error" role="alert">
                    {loadError}
                  </p>
                ) : null}

                <section className="events-calendar-card" aria-label="Events calendar">
                  <div className="events-calendar-header">
                    <div className="events-calendar-nav">
                      <button type="button" className="events-cal-nav-btn" onClick={goPrev} aria-label="Previous">
                        ‹
                      </button>
                      <button type="button" className="events-cal-today-btn" onClick={goToday}>
                        Today
                      </button>
                      <button type="button" className="events-cal-nav-btn" onClick={goNext} aria-label="Next">
                        ›
                      </button>
                      <h2 className="events-calendar-title">{calendarTitle}</h2>
                    </div>
                    <div className="events-calendar-view-toggle" role="tablist" aria-label="Calendar view">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={viewMode === 'month'}
                        className={`events-view-btn${viewMode === 'month' ? ' events-view-btn--active' : ''}`}
                        onClick={() => setViewMode('month')}
                      >
                        Month
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={viewMode === 'week'}
                        className={`events-view-btn${viewMode === 'week' ? ' events-view-btn--active' : ''}`}
                        onClick={() => setViewMode('week')}
                      >
                        Week
                      </button>
                    </div>
                  </div>

                  <div className="events-calendar-weekdays">
                    {WEEKDAYS.map((w) => (
                      <div key={w} className="events-calendar-weekday">
                        {w}
                      </div>
                    ))}
                  </div>
                  <div className={`events-calendar-grid${viewMode === 'week' ? ' events-calendar-grid--week' : ''}`}>
                    {calendarDays.map((day, idx) => {
                      const inMonth = viewMode === 'week' || day.getMonth() === refDate.getMonth();
                      const isToday = isSameLocalDay(day, today);
                      const dayEvents = eventsForDay(day);
                      return (
                        <div
                          key={idx}
                          className={`events-calendar-cell${inMonth ? '' : ' events-calendar-cell--muted'}${
                            isToday ? ' events-calendar-cell--today' : ''
                          }`}
                        >
                          <div className="events-calendar-daynum">{day.getDate()}</div>
                          <div className="events-calendar-day-events">
                            {dayEvents.map((ev) => (
                              <button
                                key={ev.id}
                                type="button"
                                className="events-calendar-chip"
                                style={{
                                  backgroundColor: `hsl(${eventHue(ev.id)}, 70%, 92%)`,
                                  borderColor: `hsl(${eventHue(ev.id)}, 60%, 70%)`,
                                }}
                                title={ev.name}
                                onClick={() => navigate(`/events/${ev.id}`)}
                              >
                                {ev.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>

                {loading ? (
                  <p className="page-subtitle">Loading events…</p>
                ) : (
                  <>
                    <EventsTable
                      title="Active Events"
                      sectionId="active"
                      events={active}
                      emptyText="No active events."
                      onRowClick={(id) => navigate(`/events/${id}`)}
                    />
                    <EventsTable
                      title="Past Events"
                      sectionId="past"
                      events={past}
                      emptyText="No past events."
                      onRowClick={(id) => navigate(`/events/${id}`)}
                    />
                    <EventsTable
                      title="Void Events"
                      sectionId="void"
                      events={voided}
                      emptyText="No void events."
                      onRowClick={(id) => navigate(`/events/${id}`)}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Create event" wide>
        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="event-new-name" className="form-label">
              Event name
            </label>
            <input
              id="event-new-name"
              type="text"
              className="form-input"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
                setAddError('');
              }}
              placeholder="e.g. NRA Show 2026"
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="event-new-location" className="form-label">
              Event location
            </label>
            <input
              id="event-new-location"
              type="text"
              className="form-input"
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              placeholder="e.g. McCormick Place, Chicago"
              disabled={addSubmitting}
            />
          </div>
          <div className="events-form-row">
            <div className="form-group">
              <label htmlFor="event-new-start-date" className="form-label">
                Start date
              </label>
              <input
                id="event-new-start-date"
                type="date"
                className="form-input"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="event-new-end-date" className="form-label">
                End date
              </label>
              <input
                id="event-new-end-date"
                type="date"
                className="form-input"
                value={newEndDate}
                onChange={(e) => setNewEndDate(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
          </div>
          <div className="events-form-row">
            <div className="form-group">
              <label htmlFor="event-new-start-time" className="form-label">
                Start time
              </label>
              <input
                id="event-new-start-time"
                type="time"
                className="form-input"
                value={newStartTime}
                onChange={(e) => setNewStartTime(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="event-new-end-time" className="form-label">
                End time
              </label>
              <input
                id="event-new-end-time"
                type="time"
                className="form-input"
                value={newEndTime}
                onChange={(e) => setNewEndTime(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
          </div>
          {addError ? (
            <p className="create-order-error" role="alert">
              {addError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={addSubmitting} onClick={() => setAddOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={addSubmitting} onClick={() => void submitAdd()}>
            {addSubmitting ? 'Creating…' : 'Create event'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default Events;
